const NUMERIC_ID_PATTERN = /^\d+$/;
const ISO_TIMESTAMP_PATTERN =
  /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,9}))?(Z|[+-]\d{2}:\d{2})$/;

export const getPersistedMessageId = id => {
  let numericId = Number.NaN;
  if (typeof id === 'number') {
    numericId = id;
  } else if (typeof id === 'string' && NUMERIC_ID_PATTERN.test(id)) {
    numericId = Number(id);
  }

  return Number.isSafeInteger(numericId) && numericId > 0 ? numericId : null;
};

const parseTimestampInMicroseconds = createdAt => {
  if (
    typeof createdAt === 'number' ||
    (typeof createdAt === 'string' && createdAt.trim() !== '')
  ) {
    const numericTimestamp = Number(createdAt);
    if (Number.isFinite(numericTimestamp)) return numericTimestamp * 1_000_000;
  }

  if (typeof createdAt !== 'string') return null;

  const isoParts = createdAt.match(ISO_TIMESTAMP_PATTERN);
  if (isoParts) {
    const [, dateTime, fractionalSeconds = '', timezone] = isoParts;
    const wholeSecond = Date.parse(`${dateTime}${timezone}`);
    if (!Number.isNaN(wholeSecond)) {
      const microseconds = Number(fractionalSeconds.slice(0, 6).padEnd(6, '0'));
      return wholeSecond * 1000 + microseconds;
    }
  }

  const parsedTimestamp = Date.parse(createdAt);
  return Number.isNaN(parsedTimestamp) ? null : parsedTimestamp * 1000;
};

const getMessageTimestamp = message => {
  const preciseTimestamp =
    message.created_at_cursor ?? message.createdAtCursor ?? null;

  return (
    parseTimestampInMicroseconds(preciseTimestamp) ??
    parseTimestampInMicroseconds(
      message.created_at ?? message.createdAt ?? null
    ) ??
    Number.MAX_SAFE_INTEGER
  );
};

export const compareMessagesChronologically = (messageA, messageB) => {
  const createdAtDifference =
    getMessageTimestamp(messageA) - getMessageTimestamp(messageB);
  if (createdAtDifference !== 0) return createdAtDifference;

  const messageAId = getPersistedMessageId(messageA.id);
  const messageBId = getPersistedMessageId(messageB.id);
  if (messageAId !== null && messageBId !== null)
    return messageAId - messageBId;
  if (messageAId !== null) return -1;
  if (messageBId !== null) return 1;

  return String(messageA.id).localeCompare(String(messageB.id));
};

export const sortMessagesChronologically = messages =>
  [...messages].sort(compareMessagesChronologically);

export const getHighestPersistedMessageId = messages => {
  return messages.reduce((highestId, message) => {
    const messageId = getPersistedMessageId(message.id);
    return messageId !== null && (highestId === null || messageId > highestId)
      ? messageId
      : highestId;
  }, null);
};
